import styled from 'styled-components';
import CustomBlockButton from '../../../../../components/button/CustomBlockButton';
import { useState } from 'react';
import { useSelectedReturnExchangesActionsContextHook, useSelectedReturnExchangesValueContextHook } from '../../contexts/SelectedReturnExchangesContext';
import { customToast } from '../../../../../components/toast/custom-react-toastify/v1';
import { ModalSelectArchive } from './modals/modal-select-archive/modal-select-archive';
import { ModalDeleteItems } from './modals/modal-delete-items/modal-delete-items';
import { ModalExcelDownload } from './modals/modal-excel-download';
import { ModalCopyCreate } from './modals/modal-copy-create';
import { ModalReturnConfirm } from './modals/modal-return-confirm';
import { ModalExchangeConfirm } from './modals/modal-exchange-confirm';

const StyledContainer = styled.div`
    padding: 12px 16px;
`;

const StyledWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;

    & button{
        width: fit-content;
        height: 30px;
        padding: 0 12px;
        border-radius: 6px;
        border: 1px solid #d5d9df;
        background: #ffffff;
        color: #4b5563;
        font-size: 12px;
        font-weight: 600;
        white-space: nowrap;
        transition: all 0.15s ease;

        &:hover{
            background: #f6f8fb;
            border-color: #c2c7cf;
        }

        &.clear{
            border-color: var(--redColor1);
            color: var(--redColor1);
            background: #fff;

            &:hover{
                background: var(--destructive-muted);
            }
        }

        &.system{
            background: var(--primary);
            color: #fff;
            border: 1px solid var(--primary);

            &:hover{
                filter: brightness(0.95);
                background: var(--primary);
                color: #fff;
            }
        }

        &.delete{
            border-color: var(--destructive);
            color: var(--destructive);
            background: #fff;

            &:hover{
                background: var(--destructive);
                color: #fff;
            }
        }
    }
`;

export function WegetEventButtons(props) {
    const selectedReturnExchangesActionsContextHook = useSelectedReturnExchangesActionsContextHook();
    const selectedReturnExchangesValueContextHook = useSelectedReturnExchangesValueContextHook();
    const selectedReturnExchanges = selectedReturnExchangesValueContextHook.selectedReturnExchanges;

    const [archiveChangeModalOpen, setArchiveChangeModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [excelDownloadModalOpen, setExcelDownloadModalOpen] = useState(false);
    const [copyCreateModalOpen, setCopyCreateModalOpen] = useState(false);
    const [returnConfirmModalOpen, setReturnConfirmModalOpen] = useState(false);
    const [exchangeConfirmModalOpen, setExchangeConfirmModalOpen] = useState(false);

    const handleArchiveChangeModalOpen = () => {
        if (selectedReturnExchanges?.length === 0) {
            customToast.error('선택된 반품/교환건이 없습니다.');
            return;
        }
        setArchiveChangeModalOpen(true);
    }

    const handleArchiveChangeModalClose = () => {
        setArchiveChangeModalOpen(false);
    }

    const handleDeleteModalOpen = () => {
        if (selectedReturnExchanges?.length === 0) {
            customToast.error('선택된 반품/교환건이 없습니다.');
            return;
        }
        setDeleteModalOpen(true);
    }

    const handleDeleteModalClose = () => {
        setDeleteModalOpen(false);
    }

    const handleExcelDownloadModalOpen = () => {
        if (selectedReturnExchanges?.length === 0) {
            customToast.error('선택된 반품/교환건이 없습니다.');
            return;
        }
        setExcelDownloadModalOpen(true);
    }

    const handleExcelDownloadModalClose = () => {
        setExcelDownloadModalOpen(false);
    }

    const handleCopyCreateModalOpen = () => {
        if (selectedReturnExchanges?.length === 0) {
            customToast.error('선택된 반품/교환건이 없습니다.');
            return;
        }
        setCopyCreateModalOpen(true);
    }

    const handleCopyCreateModalClose = () => {
        setCopyCreateModalOpen(false);
    }

    const handleClearSelected = () => {
        selectedReturnExchangesActionsContextHook.selectedReturnExchanges.onSet([]);
    }

    const handleReturnConfirmModalOpen = () => {
        if (selectedReturnExchanges?.length === 0) {
            customToast.error('선택된 반품/교환건이 없습니다.');
            return;
        }

        if(selectedReturnExchanges?.length >10){
            customToast.error('반품 승인은 한번에 최대 10건까지 가능합니다.');
            return;
        }

        const copiedItems = [];
        const noMatchedStoreItems = [];
        selectedReturnExchanges?.forEach(item => {
            if (item?.copiedFlag) {
                copiedItems.push(item);
                return;
            }

            if (!item?.salesChannelMatchingStoreId) {
                noMatchedStoreItems.push(item);
                return;
            }
        })

        if (copiedItems.length > 0) {
            customToast.error(`복사된 건은 반품 승인 처리할 수 없습니다.\n\n${copiedItems?.map(r => r.orderNumber1).join('\n')}\n위 주문번호 건을 제외하고 다시 시도해주세요.`);
            return
        }

        if (noMatchedStoreItems.length > 0) {
            customToast.error(`스토어 매칭이 되지 않은 건은 반품 승인 처리할 수 없습니다.\n\n${noMatchedStoreItems?.map(r => r.orderNumber1).join('\n')}\n위 주문번호 건을 제외하고 다시 시도해주세요.`);
            return
        }
        setReturnConfirmModalOpen(true);
    }

    const handleReturnConfirmModalClose = () => {
        setReturnConfirmModalOpen(false);
    }

    const handleExchangeConfirmModalOpen = () => {
        if (selectedReturnExchanges?.length === 0) {
            customToast.error('선택된 반품/교환건이 없습니다.');
            return;
        }

        if(selectedReturnExchanges?.length >10){
            customToast.error('교환 재배송은 한번에 최대 10건까지 가능합니다.');
            return;
        }

        const copiedItems = [];
        const noMatchedStoreItems = [];
        const noDeliveryCompanyCodeItems = [];
        const noReShipmentWaybillNumberItems = [];

        selectedReturnExchanges?.forEach(item => {
            if (item?.copiedFlag) {
                copiedItems.push(item);
                return;
            }

            if (!item?.salesChannelMatchingStoreId) {
                noMatchedStoreItems.push(item);
                return;
            }

            if (!item?.exchangeReShipmentDeliveryCompanyCode) {
                noDeliveryCompanyCodeItems.push(item);
                return;
            }

            if (!item?.exchangeReShipmentWaybillNumber) {
                noReShipmentWaybillNumberItems.push(item);
                return;
            }
        })

        if (copiedItems.length > 0) {
            customToast.error(`복사된 건은 교환 배송 처리를 할 수 없습니다.\n\n${copiedItems?.map(r => r.orderNumber1).join('\n')}\n위 주문번호 건을 제외하고 다시 시도해주세요.`);
            return
        }

        if (noMatchedStoreItems.length > 0) {
            customToast.error(`스토어 매칭이 되지 않은 건은 교환 배송 처리를 할 수 없습니다.\n\n${noMatchedStoreItems?.map(r => r.orderNumber1).join('\n')}\n위 주문번호 건을 제외하고 다시 시도해주세요.`);
            return
        }

        if (noDeliveryCompanyCodeItems.length > 0) {
            customToast.error(`배송사 코드가 없는 건은 교환 배송 처리를 할 수 없습니다.\n\n${noDeliveryCompanyCodeItems?.map(r => r.orderNumber1).join('\n')}\n위 주문번호 건을 제외하고 다시 시도해주세요.`);
            return
        }

        if (noReShipmentWaybillNumberItems.length > 0) {
            customToast.error(`운송장 번호가 없는 건은 교환 배송 처리를 할 수 없습니다.\n\n${noReShipmentWaybillNumberItems?.map(r => r.orderNumber1).join('\n')}\n위 주문번호 건을 제외하고 다시 시도해주세요.`);
            return
        }

        setExchangeConfirmModalOpen(true);
    }

    const handleExchangeConfirmModalClose = () => {
        setExchangeConfirmModalOpen(false);
    }

    return (
        <>
            <StyledContainer>
                <StyledWrapper>
                    {selectedReturnExchanges?.length > 0 &&
                        <CustomBlockButton
                            type='button'
                            className='clear'
                            onClick={handleClearSelected}
                        >
                            {selectedReturnExchanges?.length}건 선택해제
                        </CustomBlockButton>
                    }
                    <CustomBlockButton
                        type='button'
                        onClick={handleArchiveChangeModalOpen}
                    >
                        보관함 이동
                    </CustomBlockButton>
                    <CustomBlockButton
                        type='button'
                        onClick={handleExcelDownloadModalOpen}
                    >
                        엑셀 다운로드
                    </CustomBlockButton>
                    <CustomBlockButton
                        type='button'
                        onClick={handleCopyCreateModalOpen}
                    >
                        복사 생성
                    </CustomBlockButton>
                    <CustomBlockButton
                        type='button'
                        className='system'
                        onClick={handleReturnConfirmModalOpen}
                    >
                        반품 승인
                    </CustomBlockButton>
                    <CustomBlockButton
                        type='button'
                        className='system'
                        onClick={handleExchangeConfirmModalOpen}
                    >
                        교환 재배송
                    </CustomBlockButton>
                    <CustomBlockButton
                        type='button'
                        className='delete'
                        onClick={handleDeleteModalOpen}
                    >
                        삭제
                    </CustomBlockButton>
                </StyledWrapper>
            </StyledContainer>

            {archiveChangeModalOpen &&
                <ModalSelectArchive
                    open={archiveChangeModalOpen}
                    onClose={handleArchiveChangeModalClose}
                />
            }

            {deleteModalOpen &&
                <ModalDeleteItems
                    open={deleteModalOpen}
                    onClose={handleDeleteModalClose}
                />
            }

            {excelDownloadModalOpen &&
                <ModalExcelDownload
                    open={excelDownloadModalOpen}
                    onClose={handleExcelDownloadModalClose}
                />
            }

            {copyCreateModalOpen &&
                <ModalCopyCreate
                    open={copyCreateModalOpen}
                    onClose={handleCopyCreateModalClose}
                />
            }

            {returnConfirmModalOpen &&
                <ModalReturnConfirm
                    open={returnConfirmModalOpen}
                    onClose={handleReturnConfirmModalClose}
                />
            }

            {exchangeConfirmModalOpen &&
                <ModalExchangeConfirm 
                    open={exchangeConfirmModalOpen}
                    onClose={handleExchangeConfirmModalClose}
                />
            }
        </>
    );
}